import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    category: "Getting Started",
    items: [
      { q: "What is Duty AI?", a: "Duty AI is an AI-powered recruiter that conducts interviews, screens resumes, and matches candidates with opportunities. Think of Duty AI as your always-available first-round interviewer." },
      { q: "How do I get started with Duty AI?", a: "Simply apply to a job that uses Duty AI. You'll receive an invitation to complete your interview via your preferred channel—video, phone, SMS, or WhatsApp." },
      { q: "Is Duty AI free for candidates?", a: "Yes, Duty AI is completely free for all candidates. Companies pay for the service so you can focus on showcasing your skills." },
      { q: "What channels can I use for my interview?", a: "Duty AI supports video calls, phone calls, SMS text conversations, and WhatsApp messaging. You can choose the channel that works best for you." }
    ]
  },
  {
    category: "The Interview",
    items: [
      { q: "How long does an Duty AI interview take?", a: "Most Duty AI interviews take between 15-30 minutes, depending on the role. You can complete them at any time that works for you—day or night." },
      { q: "Can I reschedule my interview?", a: "Since Duty AI is available 24/7, you can start your interview whenever you're ready. There's no need to schedule a specific time." },
      { q: "What happens if I lose connection during my interview?", a: "Don't worry! Duty AI saves your progress. You can resume right where you left off when you reconnect." },
      { q: "Does Duty AI understand different accents?", a: "Yes. Duty AI's AI is trained on diverse accents and dialects from around the world, supporting 26+ languages for comfortable conversations." },
      { q: "Will I know the questions in advance?", a: "The questions are based on the job requirements and are asked conversationally. Duty AI adapts follow-up questions based on your responses, just like a real interview." }
    ]
  },
  {
    category: "After the Interview",
    items: [
      { q: "How will I know my interview results?", a: "After your interview, the hiring company reviews your results and will reach out directly about next steps. Duty AI provides them with a detailed summary of your conversation." },
      { q: "Can I redo my interview?", a: "This depends on the company's policy. Some allow retakes while others accept only the first attempt. Check with the hiring company for their specific guidelines." },
      { q: "How does Duty AI evaluate my responses?", a: "Duty AI uses objective, structured criteria defined by the hiring company to evaluate responses fairly. Every candidate is assessed against the same standards." },
      { q: "Is my interview data kept private?", a: "Yes. Your interview data is handled according to our Privacy Policy. Only the hiring company you applied to can access your interview results." }
    ]
  },
  {
    category: "Technical Questions",
    items: [
      { q: "What devices can I use for my Duty AI interview?", a: "You can use any smartphone, tablet, or computer. For video interviews, ensure you have a camera and microphone. For phone interviews, any phone works." },
      { q: "Do I need to download an app?", a: "No app download is required. Video interviews work in your browser, and phone/SMS/WhatsApp interviews use the apps you already have." },
      { q: "What if I have technical issues?", a: "Contact the hiring company directly or reach out to our support team at support@dutyai.in for technical assistance." }
    ]
  }
];

const FAQItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#082900]/10">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full py-5 text-left group"
      >
        <span className="text-[#082900] text-[16px] lg:text-[18px] font-normal pr-4">{item.q}</span>
        <ChevronDown className={`w-5 h-5 text-[#082900]/40 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="pb-5 pr-12">
          <p className="text-[#082900]/70 text-[14px] lg:text-[16px] font-normal leading-relaxed">{item.a}</p>
        </div>
      )}
    </div>
  );
};

const CandidateFAQPage = () => {
  return (
    <>
      <Navbar />
      <main className="bg-[#F9FBF5] min-h-screen">
        <section className="pt-20 lg:pt-32 pb-16">
          <div className="max-w-[1121px] mx-auto px-6 lg:px-10">
            <h1 className="text-[#082900] text-[36px] lg:text-[64px] font-light leading-[1.05] tracking-[-0.011em] mb-4">
              Candidate FAQ
            </h1>
            <p className="text-[#082900]/60 text-[16px] lg:text-[18px] font-normal max-w-2xl mb-16">
              Everything you need to know about interviewing with Duty AI.
            </p>

            <div className="space-y-12">
              {faqs.map((section, i) => (
                <div key={i}>
                  <h2 className="text-[#082900] text-[20px] lg:text-[24px] font-normal mb-6">{section.category}</h2>
                  <div>
                    {section.items.map((item, j) => (
                      <FAQItem key={j} item={item} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default CandidateFAQPage;
